import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { RavenService } from '@telecom/shared/services/raven-service/raven-service';
import * as TrackEvents from '@/constants/track-events';
import { TRACK_EVENT_TYPE } from '@/constants';
import { getAgentNameByRoute } from '@telecom/shared/utils/agent-name';

const USAGE_LIMIT_UPGRADE_PATH = '/credits-price';
const USAGE_LIMIT_UPGRADE_FROM_QUERY = 'upgrade-from';
const DEFAULT_PRODUCT_LINE = 'eureka_ip_searching';
const DEFAULT_TRIGGER_SOURCE = 'direct';

/**
 * Pricing 页头部展示类型，用于区分不同升级入口的标题文案。
 */
export type UsageLimitUpgradeHeaderType =
    | 'pricing_proactive_view'
    | 'feature_upgrade_required'
    | 'credits_limit_upgrade';

export interface UsageLimitUpgradeContextOptions {
    /** 产线标识，默认 eureka_ip_searching */
    product_line?: string;
    /** 触发入口或超限场景 */
    trigger_source?: string;
    /** Agent 或功能点技术枚举 */
    agent_name?: string;
    /** 进入 Pricing 前的页面 URL，默认取 currentUrl */
    page_url?: string;
    /** Pricing 页头部展示类型 */
    header_type?: UsageLimitUpgradeHeaderType;
}

export interface UsageLimitUpgradeContext {
    /** 产线标识 */
    product_line: string;
    /** 触发入口或超限场景 */
    trigger_source: string;
    /** Agent 或功能点技术枚举 */
    agent_name: string;
    /** 进入 Pricing 前的页面 URL */
    page_url: string;
    /** Pricing 页头部展示类型 */
    header_type?: UsageLimitUpgradeHeaderType;
}

export interface GetUsageLimitUpgradeUrlOptions extends UsageLimitUpgradeContextOptions {
    /** 当前页面完整 URL，默认取 window.location.href */
    currentUrl?: string;
}

export interface JumpToUsageLimitUpgradePageOptions extends GetUsageLimitUpgradeUrlOptions {
    /** 跳转目标窗口，默认当前窗口 */
    target?: '_self' | '_blank';
    /** 自定义当前窗口跳转实现，默认设置 window.location.href */
    navigate?: (url: string) => void;
    /** 自定义打开新窗口实现，默认使用 window.open */
    open?: (url: string, target: '_blank') => Window | null;
    /** 自定义埋点实现，默认使用 RavenService */
    track?: (sourceType: string, data: Parameters<RavenService['track']>[1]) => void;
}

/**
 * 获取用量限制升级页地址
 */
export function getUsageLimitUpgradeUrl(options: GetUsageLimitUpgradeUrlOptions = {}) {
    return appendUsageLimitUpgradeContextToUrl(USAGE_LIMIT_UPGRADE_PATH, options);
}

/**
 * 给已有升级页地址补齐 Pricing 归因参数
 */
export function appendUsageLimitUpgradeContextToUrl(targetUrl: string, options: GetUsageLimitUpgradeUrlOptions = {}) {
    const currentUrl = options.currentUrl ?? window.location.href;
    const context = resolveUsageLimitUpgradeContext({
        ...options,
        currentUrl,
    });
    const url = createUrl(targetUrl);

    url.searchParams.set(USAGE_LIMIT_UPGRADE_FROM_QUERY, currentUrl);
    url.searchParams.set('product_line', context.product_line);
    url.searchParams.set('trigger_source', context.trigger_source);
    url.searchParams.set('agent_name', context.agent_name);
    url.searchParams.set('page_url', context.page_url);
    if (context.header_type) {
        url.searchParams.set('header_type', context.header_type);
    }

    if (/^https?:\/\//.test(targetUrl)) {
        return url.toString();
    }

    return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * 从当前路由或 URL 推断升级归因中的 agent_name
 */
export function inferUsageLimitAgentName(routeOrUrl?: string) {
    return getAgentNameByRoute(routeOrUrl ?? window.location.href);
}

/**
 * 解析升级页 URL 和埋点共用的上下文字段
 */
export function resolveUsageLimitUpgradeContext(options: GetUsageLimitUpgradeUrlOptions = {}): UsageLimitUpgradeContext {
    const currentUrl = options.currentUrl ?? window.location.href;

    return {
        product_line: options.product_line ?? DEFAULT_PRODUCT_LINE,
        trigger_source: options.trigger_source ?? DEFAULT_TRIGGER_SOURCE,
        agent_name: options.agent_name ?? inferUsageLimitAgentName(currentUrl),
        page_url: options.page_url ?? currentUrl,
        ...resolveHeaderTypeContext(options.header_type),
    };
}

function resolveHeaderTypeContext(headerType?: UsageLimitUpgradeHeaderType): Pick<UsageLimitUpgradeContext, 'header_type'> {
    if (headerType) {
        return {
            header_type: headerType,
        };
    }

    return {};
}

/**
 * 跳转到用量限制升级页
 */
export function jumpToUsageLimitUpgradePage(options: JumpToUsageLimitUpgradePageOptions = {}) {
    const url = getUsageLimitUpgradeUrl(options);

    trackUsageLimitUpgradeTrigger(options);

    if (options.target === '_blank') {
        const open = options.open ?? window.open.bind(window);
        open(url, '_blank');
        return url;
    }

    const navigate = options.navigate ?? ((targetUrl: string) => {
        window.location.href = targetUrl;
    });
    navigate(url);
    return url;
}

function trackUsageLimitUpgradeTrigger(options: JumpToUsageLimitUpgradePageOptions) {
    if (!options.trigger_source) return;

    const context = resolveUsageLimitUpgradeContext(options);
    const track = options.track ?? ((sourceType, data) => {
        injectService<RavenService>(RavenService).track(sourceType, data);
    });

    track(TrackEvents.TRACK_EVENT_UPGRADE_TRIGGER, {
        eventType: TRACK_EVENT_TYPE.EurekaUpgradePage,
        eventDetailStr: {
            ...context,
        },
    });
}

function createUrl(routeOrUrl: string) {
    try {
        return new URL(routeOrUrl, window.location.origin);
    } catch {
        return new URL(`https://example.com${routeOrUrl.startsWith('/') ? '' : '/'}${routeOrUrl}`);
    }
}
